import type {
  ContentId,
  GameStateSnapshot,
  GameStateName,
  Mechanic,
  PlayerId,
  ReturnTarget,
  RoomConfig,
  RoomId,
  TensionState,
  VoteChoice,
  VoteOutcome,
  VotingSnapshot,
} from '@juegazo/shared';
import { ALL_MECHANICS } from '@juegazo/shared';
import type { Clock } from './clock';
import type { Rng } from './rng';

export * from './clock';
export * from './rng';

const MAX_PLAYERS = 12;
const MIN_PLAYERS = 2;
const MIN_BOMBS = 1;
const MAX_BOMBS = 10;
const EXPLOSION_TURN_MS = 5_000;
const RETURN_WINDOW_MS = 5_000;
const DEFAULT_BASE_DURATION_MS = 30_000;
const MIN_BASE_DURATION_MS = 10_000;
const MAX_BASE_DURATION_MS = 60_000;
const EXTRA_DURATION_MS = 15_000;
const BONUS_PER_PASS_MS = 1_000;

interface InternalPlayer {
  id: PlayerId;
  displayName: string;
  totalMs: number;
  explosions: number;
  joinedAt: number;
  turnStartedAt: number | null;
}

interface LastPassSnapshot {
  previousPlayerId: PlayerId;
  previousTurnStartedAt: number;
  bonusAppliedMs: number;
  rotationOrder: PlayerId[];
  rotationIndex: number;
  roundNumber: number;
  remainingTimeMsAtPass: number;
}

export interface ContentProvider {
  list(mechanic: Mechanic): ReadonlyArray<{
    id: ContentId;
    mechanic: Mechanic;
    enabled: boolean;
    difficulty: 'easy' | 'medium' | 'hard';
  }>;
  byId(id: ContentId): {
    id: ContentId;
    mechanic: Mechanic;
    enabled: boolean;
    difficulty: 'easy' | 'medium' | 'hard';
  } | null;
}

export interface CreateGameOptions {
  roomId: RoomId;
  totalBombs?: number;
  enabledMechanics?: readonly Mechanic[];
  mixedMode?: boolean;
  baseDurationMs?: number;
  rng: Rng;
  clock: Clock;
  contentProvider?: ContentProvider;
}

export interface GameHandle {
  addPlayer(id: PlayerId, displayName: string): void;
  removePlayer(id: PlayerId): void;
  setContentProvider(provider: ContentProvider): void;
  configure(cfg: Partial<RoomConfig>): void;
  start(): void;
  startBomb(): void;
  passBomb(): void;
  returnBomb(): void;
  pause(): void;
  resume(): void;
  changePrompt(): void;
  explode(): void;
  advanceBetweenBombs(): void;
  earlyFinish(): void;
  startTiebreak(): void;
  startVote(initiatorId: PlayerId): void;
  castVote(playerId: PlayerId, choice: VoteChoice): void;
  decideBombReturn(actorId: PlayerId, target: ReturnTarget): void;
  clearVote(): void;
  tick(): void;
  peek(): { remainingTimeMs: number; state: GameStateName; currentPlayerId: PlayerId | null; tensionState: TensionState; paused: boolean };
  advanceAndTick(): { before: { remainingTimeMs: number; state: GameStateName; currentPlayerId: PlayerId | null; paused: boolean }; after: { remainingTimeMs: number; state: GameStateName; currentPlayerId: PlayerId | null; paused: boolean }; changed: boolean };
  getState(): GameStateSnapshot;
  getHistory(): { roundRotations: PlayerId[][] };
  getScores(): { playerId: PlayerId; displayName: string; totalMs: number; explosions: number }[];
  getFinalResult(): {
    ranking: { playerId: PlayerId; displayName: string; totalMs: number; explosions: number }[];
    winnerId: PlayerId | null;
    exactTie: boolean;
    tiedPlayerIds: PlayerId[];
    finished: boolean;
  };
}

interface CreateGameResult extends GameHandle {}

const shuffle = <T>(arr: T[], rng: Rng): T[] => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const buildPromptLines = (
  mechanic: Mechanic | null,
  content: { id: ContentId; mechanic: Mechanic; [k: string]: unknown } | null,
  lastChainWord: string | null,
): string[] => {
  if (!mechanic || !content) return [];
  const lines: string[] = [];
  switch (mechanic) {
    case 'category':
      lines.push(String(content['category'] ?? '').toUpperCase());
      break;
    case 'starts_with_letter':
      // lines.push(`LETRA ${String(content['letter'] ?? '').toUpperCase()}`);
      lines.push(`${String(content['letter'] ?? '').toUpperCase()}`);
      break;
    case 'contains':
      lines.push(`"${String(content['sequence'] ?? '').toUpperCase()}"`);
      break;
    case 'excludes_letter':
      lines.push(String(content['category'] ?? '').toUpperCase());
      // lines.push(`LETRA ${String(content['letter'] ?? '').toUpperCase()}`);
      lines.push(`${String(content['letter'] ?? '').toUpperCase()}`);
      break;
    case 'chain':
      lines.push((lastChainWord ?? String(content['startWord'] ?? '')).toUpperCase());
      break;
    case 'dual_condition':
      lines.push(String(content['category'] ?? '').toUpperCase());
      // lines.push(`LETRA ${String(content['letter'] ?? '').toUpperCase()}`);
      lines.push(`${String(content['letter'] ?? '').toUpperCase()}`);
      break;
    case 'name_three':
      lines.push(`3 ${String(content['category'] ?? '').toUpperCase()}`);
      break;
  }
  return lines;
};

export const createGame = (opts: CreateGameOptions): CreateGameResult => {
  let totalBombs = opts.totalBombs ?? 5;
  if (totalBombs < MIN_BOMBS || totalBombs > MAX_BOMBS) {
    throw new Error(`Invalid totalBombs: ${totalBombs}`);
  }
  const enabledMechanics = (opts.enabledMechanics ?? [...ALL_MECHANICS]).slice();
  if (enabledMechanics.length === 0) {
    throw new Error('At least one mechanic must be enabled');
  }
  let mixedMode = opts.mixedMode ?? false;
  let baseDurationMs = opts.baseDurationMs ?? DEFAULT_BASE_DURATION_MS;
  if (baseDurationMs < MIN_BASE_DURATION_MS || baseDurationMs > MAX_BASE_DURATION_MS) {
    throw new Error(`Invalid baseDurationMs: ${baseDurationMs}`);
  }
  const rng = opts.rng;
  const clock = opts.clock;

  const players = new Map<PlayerId, InternalPlayer>();
  let contentProvider: ContentProvider | null = opts.contentProvider ?? null;

  let state: GameStateName = 'lobby';
  let bombNumber = 0;
  let currentRound = 1;
  let currentPlayerId: PlayerId | null = null;
  let rotationOrder: PlayerId[] = [];
  let rotationIndex = 0;
  let initialTimeMs = 0;
  let bombEndsAt: number | null = null;
  let tensionState: TensionState = 'normal';
  let selectedMechanic: Mechanic | null = null;
  let selectedContentId: ContentId | null = null;
  let promptLines: string[] = [];
  let paused = false;
  let lastPassSnapshot: LastPassSnapshot | null = null;
  let returnWindowExpiresAt: number | null = null;
  let voting: VotingSnapshot | null = null;
  let lastForcedPassTarget: PlayerId | null = null;
  let lastExplosionPlayerId: PlayerId | null = null;
  let tiebreakPlayers: PlayerId[] = [];
  let lastChainWord: string | null = null;
  let usedContentIds = new Set<ContentId>();
  const roundRotations: PlayerId[][] = [];
  let gameFinished = false;

  const tensionThresholds = { acelerado: 0, inminente: 0 };

  let effectiveTime = 0;
  let lastTickWallTime = clock.now();

  const computeRemainingMs = (): number => {
    if (bombEndsAt === null) return 0;
    return Math.max(0, bombEndsAt - effectiveTime);
  };

  const recomputeTension = (): TensionState => {
    const remaining = computeRemainingMs();
    if (remaining < tensionThresholds.inminente) return 'inminente';
    if (remaining <= tensionThresholds.acelerado) return 'acelerado';
    return 'normal';
  };

  const updateTensionMonotonic = (next: TensionState) => {
    const order: TensionState[] = ['normal', 'acelerado', 'inminente'];
    if (order.indexOf(next) > order.indexOf(tensionState)) {
      tensionState = next;
    }
  };

  const generateRotation = (excludeSet: ReadonlySet<PlayerId>): PlayerId[] => {
    const baseIds = Array.from(players.keys()).filter((id) =>
      tiebreakPlayers.length === 0 ? true : tiebreakPlayers.includes(id),
    );
    if (tiebreakPlayers.length > 0) {
      return shuffle(baseIds, rng);
    }
    const excluded = baseIds.filter((id) => excludeSet.has(id));
    const rest = baseIds.filter((id) => !excludeSet.has(id));
    if (excluded.length > 0 && rest.length >= 1) {
      return [...shuffle(rest, rng), ...shuffle(excluded, rng)];
    }
    return shuffle(baseIds, rng);
  };

  const selectMechanic = (): Mechanic => {
    if (!mixedMode || enabledMechanics.length === 1) {
      return enabledMechanics[0];
    }
    const candidates = enabledMechanics.filter((m) => m !== selectedMechanic);
    const pool = candidates.length > 0 ? candidates : enabledMechanics;
    const idx = Math.floor(rng.next() * pool.length);
    return pool[Math.min(idx, pool.length - 1)];
  };

  const selectContent = (
    mechanic: Mechanic,
  ): { id: ContentId; mechanic: Mechanic; [k: string]: unknown } | null => {
    if (!contentProvider) return null;
    const all = contentProvider.list(mechanic).filter((i) => i.enabled);
    const unused = all.filter((i) => !usedContentIds.has(i.id));
    const pool = unused.length > 0 ? unused : all;
    if (pool.length === 0) return null;
    const idx = Math.floor(rng.next() * pool.length);
    return pool[Math.min(idx, pool.length - 1)] as { id: ContentId; mechanic: Mechanic; [k: string]: unknown };
  };

  const computeRanking = () => {
    const entries = Array.from(players.values()).map((p) => ({
      playerId: p.id,
      displayName: p.displayName,
      totalMs: p.totalMs,
      explosions: p.explosions,
    }));
    return entries.sort(
      (a, b) =>
        a.explosions - b.explosions ||
        a.totalMs - b.totalMs ||
        a.displayName.localeCompare(b.displayName),
    );
  };

  const detectTie = (ranking: ReturnType<typeof computeRanking>) => {
    if (ranking.length === 0) return { exactTie: false, tied: [] as PlayerId[] };
    const top = ranking[0];
    const tied = ranking
      .filter((r) => r.explosions === top.explosions && r.totalMs === top.totalMs)
      .map((r) => r.playerId);
    return { exactTie: tied.length > 1, tied };
  };

  const buildSnapshot = (): GameStateSnapshot => {
    const playerSnapshots = Array.from(players.values())
      .sort((a, b) => a.joinedAt - b.joinedAt)
      .map((p) => ({
        id: p.id,
        displayName: p.displayName,
        isAdmin: false,
        isConnected: true,
        joinedAt: p.joinedAt,
      }));
    return {
      roomId: opts.roomId,
      state,
      bombNumber,
      totalBombs,
      currentRound,
      currentPlayerId,
      rotationOrder: rotationOrder.slice(),
      rotationIndex,
      lastForcedPassTarget,
      remainingTimeMs: computeRemainingMs(),
      initialTimeMs,
      tensionState,
      selectedMechanic,
      selectedContentId,
      promptLines: promptLines.slice(),
      paused,
      pausedBy: null,
      countdownEndsAt: null,
      bombEndsAt,
      returnWindowExpiresAt,
      returnWindowMsRemaining:
        returnWindowExpiresAt === null ? 0 : Math.max(0, returnWindowExpiresAt - effectiveTime),
      voting: voting
        ? {
            initiatorId: voting.initiatorId,
            votes: { ...voting.votes },
            phase: voting.phase,
            outcome: voting.outcome,
            returnDecision: voting.returnDecision ? { ...voting.returnDecision } : null,
          }
        : null,
      lastExplosionPlayerId,
      tiebreakPlayers: tiebreakPlayers.slice(),
      winnerId: null,
      players: playerSnapshots,
      enabledMechanics: enabledMechanics.slice(),
      mixedMode,
      baseDurationMs,
    };
  };

  const startBombInternal = () => {
    bombNumber++;
    currentRound = 1;
    selectedMechanic = selectMechanic();
    const content = selectContent(selectedMechanic);
    selectedContentId = content?.id ?? null;
    if (selectedContentId) usedContentIds.add(selectedContentId);
    promptLines = buildPromptLines(selectedMechanic, content, lastChainWord);
    if (content && content['startWord']) {
      lastChainWord = String(content['startWord']);
    }

    const r0 = rng.next();
    const initial = baseDurationMs + Math.round(r0 * EXTRA_DURATION_MS);
    initialTimeMs = initial;
    bombEndsAt = effectiveTime + initial;

    const pAc = 0.45 + rng.next() * 0.1;
    const pIn = 0.15 + rng.next() * 0.1;
    tensionThresholds.acelerado = Math.floor(initial * pAc);
    tensionThresholds.inminente = Math.floor(initial * pIn);
    tensionState = 'normal';

    rotationOrder = generateRotation(new Set());
    rotationIndex = 0;
    currentPlayerId = rotationOrder[0] ?? null;
    roundRotations.push(rotationOrder.slice());

    const active = currentPlayerId ? players.get(currentPlayerId) : null;
    if (active) active.turnStartedAt = effectiveTime;

    lastPassSnapshot = null;
    returnWindowExpiresAt = null;
    state = 'playing';
  };

  const advanceToNextPlayer = () => {
    if (currentPlayerId) {
      const active = players.get(currentPlayerId);
      if (active) active.turnStartedAt = null;
    }

    while (true) {
      rotationIndex++;
      if (rotationIndex >= rotationOrder.length) {
        currentRound++;
        const last = currentPlayerId;
        const excludeSet = new Set<PlayerId>();
        if (last) excludeSet.add(last);
        if (lastForcedPassTarget) excludeSet.add(lastForcedPassTarget);
        rotationOrder = generateRotation(excludeSet);
        rotationIndex = 0;
        roundRotations.push(rotationOrder.slice());
        lastForcedPassTarget = null; // reset for new round
      }
      const candidate = rotationOrder[rotationIndex];
      if (candidate && candidate === lastForcedPassTarget) {
        continue; // skip: X already had the bomb this round via forced pass
      }
      currentPlayerId = candidate ?? null;
      break;
    }

    if (currentPlayerId) {
      const active = players.get(currentPlayerId);
      if (active) active.turnStartedAt = effectiveTime;
    }
  };

  const tickTime = () => {
    const wallNow = clock.now();
    if (!paused) {
      const delta = Math.max(0, wallNow - lastTickWallTime);
      effectiveTime += delta;
    }
    lastTickWallTime = wallNow;
  };

  const checkExplosion = () => {
    if (state !== 'playing' && state !== 'tiebreak_playing') return;
    if (computeRemainingMs() <= 0) {
      doExplode();
    }
  };

  const doExplode = () => {
    if (currentPlayerId) {
      const player = players.get(currentPlayerId);
      if (player) {
        player.explosions++;
        player.turnStartedAt = null;
      }
      lastExplosionPlayerId = currentPlayerId;
    }
    state = 'exploded';
  };

  const resolveVote = () => {
    if (!voting) return;
    let cVal = 0;
    let cNoVal = 0;
    for (const choice of Object.values(voting.votes)) {
      if (choice === 'valida') cVal++;
      else cNoVal++;
    }
    let outcome: VoteOutcome;
    if (cVal > cNoVal) outcome = 'valida';
    else if (cNoVal > cVal) outcome = 'no_valida';
    else {
      // tie → rng decides
      outcome = rng.next() < 0.5 ? 'valida' : 'no_valida';
    }
    voting.phase = 'decided';
    voting.outcome = outcome;
  };

  const applyForcedPass = (target: PlayerId) => {
    const previousPlayerId = currentPlayerId;
    const previousPlayer = previousPlayerId ? players.get(previousPlayerId) : null;
    const previousTurnStartedAt = previousPlayer?.turnStartedAt ?? effectiveTime;
    const remainingAtPass = computeRemainingMs();
    const bonus = BONUS_PER_PASS_MS;
    if (bombEndsAt !== null) bombEndsAt += bonus;
    if (previousPlayer) previousPlayer.turnStartedAt = null;
    currentPlayerId = target;
    const newActive = players.get(target);
    if (newActive) newActive.turnStartedAt = effectiveTime;
    returnWindowExpiresAt = effectiveTime + RETURN_WINDOW_MS;
    lastPassSnapshot = {
      previousPlayerId: previousPlayerId ?? target,
      previousTurnStartedAt,
      bonusAppliedMs: bonus,
      rotationOrder: rotationOrder.slice(),
      rotationIndex,
      roundNumber: currentRound,
      remainingTimeMsAtPass: remainingAtPass,
    };
    lastForcedPassTarget = target;
  };

  const handle: GameHandle = {
    addPlayer(id, displayName) {
      if (players.has(id)) return;
      players.set(id, {
        id,
        displayName,
        totalMs: 0,
        explosions: 0,
        joinedAt: clock.now(),
        turnStartedAt: null,
      });
    },
    removePlayer(id) {
      players.delete(id);
      rotationOrder = rotationOrder.filter((p) => p !== id);
      if (tiebreakPlayers.length > 0) {
        tiebreakPlayers = tiebreakPlayers.filter((p) => p !== id);
      }
    },
    setContentProvider(provider) {
      contentProvider = provider;
    },
    configure(cfg) {
      if (cfg.totalBombs !== undefined) {
        if (cfg.totalBombs < MIN_BOMBS || cfg.totalBombs > MAX_BOMBS) throw new Error('Invalid totalBombs');
        totalBombs = cfg.totalBombs;
      }
      if (cfg.enabledMechanics !== undefined) {
        if (cfg.enabledMechanics.length === 0) throw new Error('At least one mechanic required');
        enabledMechanics.length = 0;
        (cfg.enabledMechanics as Mechanic[]).forEach((m) => enabledMechanics.push(m));
      }
      if (cfg.mixedMode !== undefined) {
        mixedMode = cfg.mixedMode;
      }
      if (cfg.baseDurationMs !== undefined) {
        if (cfg.baseDurationMs < MIN_BASE_DURATION_MS || cfg.baseDurationMs > MAX_BASE_DURATION_MS) {
          throw new Error('Invalid baseDurationMs');
        }
        baseDurationMs = cfg.baseDurationMs;
      }
    },
    start() {
      if (players.size < MIN_PLAYERS || players.size > MAX_PLAYERS) {
        throw new Error(`Players must be between ${MIN_PLAYERS} and ${MAX_PLAYERS}`);
      }
      if (enabledMechanics.length === 0) throw new Error('At least one mechanic required');
      state = 'lobby';
      bombNumber = 0;
      gameFinished = false;
      lastExplosionPlayerId = null;
      tiebreakPlayers = [];
      usedContentIds = new Set();
    },
    startBomb() {
      if (players.size < MIN_PLAYERS) throw new Error('Not enough players');
      startBombInternal();
    },
    passBomb() {
      tickTime();
      if (voting !== null) throw new Error('Cannot pass bomb while vote is open');
      if (state !== 'playing') throw new Error('Cannot pass bomb in current state');
      if (currentPlayerId === null) throw new Error('No active player');
      if (computeRemainingMs() <= 0) throw new Error('Bomb already exploded');
      const player = players.get(currentPlayerId);
      if (!player) throw new Error('Player not found');
      if (player.turnStartedAt === null) player.turnStartedAt = effectiveTime;
      const duration = effectiveTime - player.turnStartedAt;
      player.totalMs += duration;
      const previousTurnStartedAt = player.turnStartedAt;

      const bonus = BONUS_PER_PASS_MS;
      const remainingAtPass = computeRemainingMs();
      if (bombEndsAt !== null) bombEndsAt += bonus;

      lastPassSnapshot = {
        previousPlayerId: currentPlayerId,
        previousTurnStartedAt,
        bonusAppliedMs: bonus,
        rotationOrder: rotationOrder.slice(),
        rotationIndex,
        roundNumber: currentRound,
        remainingTimeMsAtPass: remainingAtPass,
      };
      returnWindowExpiresAt = effectiveTime + RETURN_WINDOW_MS;

      advanceToNextPlayer();
    },
    returnBomb() {
      tickTime();
      if (voting !== null) throw new Error('Cannot return bomb while vote is open');
      if (state !== 'playing') throw new Error('Cannot return bomb in current state');
      if (!lastPassSnapshot) throw new Error('No pass to undo');
      if (returnWindowExpiresAt === null || effectiveTime >= returnWindowExpiresAt) {
        throw new Error('Return window expired');
      }
      const snap = lastPassSnapshot;
      const previous = players.get(snap.previousPlayerId);
      if (!previous) throw new Error('Previous player not found');
      if (currentPlayerId) {
        const current = players.get(currentPlayerId);
        if (current) current.turnStartedAt = null;
      }
      if (bombEndsAt !== null) bombEndsAt -= snap.bonusAppliedMs;
      currentPlayerId = snap.previousPlayerId;
      rotationOrder = snap.rotationOrder.slice();
      rotationIndex = snap.rotationIndex;
      currentRound = snap.roundNumber;
      previous.turnStartedAt = effectiveTime;
      lastPassSnapshot = null;
      returnWindowExpiresAt = null;
      updateTensionMonotonic(recomputeTension());
    },
    pause() {
      tickTime();
      if (state !== 'playing') throw new Error('Can only pause while playing');
      paused = true;
      state = 'paused';
    },
    resume() {
      if (voting !== null) throw new Error('Resolve the vote before resuming');
      if (state !== 'paused') throw new Error('Can only resume from paused');
      paused = false;
      state = 'playing';
    },
    changePrompt() {
      if (!paused) throw new Error('Can only change prompt while paused');
      if (!selectedMechanic) throw new Error('No active prompt');
      const content = selectContent(selectedMechanic);
      selectedContentId = content?.id ?? null;
      if (selectedContentId) usedContentIds.add(selectedContentId);
      promptLines = buildPromptLines(selectedMechanic, content, lastChainWord);
    },
    explode() {
      tickTime();
      doExplode();
    },
    advanceBetweenBombs() {
      if (state !== 'exploded') throw new Error('Not in exploded state');
      if (bombNumber >= totalBombs) {
        const ranking = computeRanking();
        const { exactTie, tied } = detectTie(ranking);
        if (exactTie) {
          tiebreakPlayers = tied;
          state = 'tiebreak_ready';
          gameFinished = true;
        } else {
          state = 'finished';
          gameFinished = true;
        }
        return;
      }
      state = 'between_bombs';
    },
    earlyFinish() {
      const ranking = computeRanking();
      if (ranking.length === 0 || ranking.every((r) => r.totalMs === 0 && r.explosions === 0)) {
        gameFinished = true;
        state = 'finished';
        return;
      }
      gameFinished = true;
      state = 'finished';
    },
    startTiebreak() {
      if (state !== 'tiebreak_ready' && state !== 'finished') {
        throw new Error('Not in tiebreak-ready state');
      }
      const ranks = computeRanking();
      const top = ranks[0];
      tiebreakPlayers = top
        ? ranks
            .filter((r) => r.explosions === top.explosions && r.totalMs === top.totalMs)
            .map((r) => r.playerId)
        : [];
      if (tiebreakPlayers.length < 2) throw new Error('No tiebreak needed');
      lastChainWord = null;
      usedContentIds = new Set();
      startBombInternal();
      state = 'tiebreak_playing';
    },
    startVote(initiatorId) {
      tickTime();
      if (state !== 'paused') throw new Error('Can only start vote while paused');
      if (voting !== null) throw new Error('Vote already in progress');
      if (!lastPassSnapshot) throw new Error('No pass to contest');
      voting = {
        initiatorId,
        votes: {},
        phase: 'casting',
        outcome: null,
        returnDecision: null,
      };
    },
    castVote(playerId, choice) {
      tickTime();
      if (!voting) throw new Error('No vote in progress');
      if (voting.phase !== 'casting') throw new Error('Vote already resolved');
      if (!players.has(playerId)) throw new Error('Player not in game');
      voting.votes[playerId] = choice;
      // Mayoría absoluta sobre TODOS los participantes, contando solo una
      // opción. Con 5 jugadores se necesitan 3 VÁLIDOS (o 3 INVALIDOS) para
      // decidir; un reparto 2-1 (o similar) NO resuelve todavía.
      const totalPlayers = players.size;
      const majority = Math.floor(totalPlayers / 2) + 1;
      let cVal = 0;
      let cNoVal = 0;
      for (const v of Object.values(voting.votes)) {
        if (v === 'valida') cVal++;
        else cNoVal++;
      }
      // Resuelve cuando una opción alcanza la mayoría absoluta (sobre todos
      // los participantes, p.ej. 3 de 5). También resuelve si TODOS ya votaron
      // pero ninguna opción llegó a la mayoría absoluta (p.ej. empate 2-2 con
      // 4 jugadores), para que la votación nunca quede colgada.
      const everyoneVoted = Object.keys(voting.votes).length >= totalPlayers;
      if (cVal >= majority || cNoVal >= majority || everyoneVoted) {
        resolveVote();
      }
    },
    decideBombReturn(actorId, target) {
      tickTime();
      if (!voting) throw new Error('No vote in progress');
      if (voting.phase !== 'decided') throw new Error('Vote not yet resolved');
      if (voting.outcome !== 'no_valida') throw new Error('Vote did not resolve to no_valida');
      if (voting.returnDecision !== null) throw new Error('Decision already made');
      if (typeof target !== 'string') {
        throw new Error('Target must be a PlayerId or "no_return"');
      }
      if (target !== 'no_return') {
        if (!players.has(target)) throw new Error('Target player not in game');
        if (target === currentPlayerId) throw new Error('Cannot return to active player');
        applyForcedPass(target);
      }
      voting.returnDecision = { actorId, target };
    },
    clearVote() {
      tickTime();
      if (!voting) throw new Error('No vote in progress');
      if (voting.phase !== 'decided') throw new Error('Vote not yet resolved');
      voting = null;
    },
    tick() {
      tickTime();
      if (state === 'playing' || state === 'tiebreak_playing') {
        updateTensionMonotonic(recomputeTension());
      }
      checkExplosion();
    },
    peek() {
      tickTime();
      return {
        remainingTimeMs: computeRemainingMs(),
        state,
        currentPlayerId,
        tensionState,
        paused,
      };
    },
    advanceAndTick() {
      const beforeRemaining = computeRemainingMs();
      const beforeState = state;
      const beforePlayer = currentPlayerId;
      const beforePaused = paused;
      tickTime();
      if (state === 'playing' || state === 'tiebreak_playing') {
        updateTensionMonotonic(recomputeTension());
      }
      checkExplosion();
      const afterRemaining = computeRemainingMs();
      const changed =
        beforeRemaining !== afterRemaining ||
        beforeState !== state ||
        beforePlayer !== currentPlayerId ||
        beforePaused !== paused;
      return {
        before: { remainingTimeMs: beforeRemaining, state: beforeState, currentPlayerId: beforePlayer, paused: beforePaused },
        after: { remainingTimeMs: afterRemaining, state, currentPlayerId, paused },
        changed,
      };
    },
    getState() {
      tickTime();
      if (state === 'playing' || state === 'tiebreak_playing') {
        updateTensionMonotonic(recomputeTension());
      }
      checkExplosion();
      const snap = buildSnapshot();
      if (gameFinished && state === 'finished') {
        const ranking = computeRanking();
        const { exactTie, tied } = detectTie(ranking);
        if (exactTie) {
          snap.tiebreakPlayers = tied.slice();
        } else {
          snap.winnerId = ranking[0]?.playerId ?? null;
        }
      }
      return snap;
    },
    getHistory() {
      return { roundRotations: roundRotations.slice() };
    },
    getScores() {
      return Array.from(players.values()).map((p) => ({
        playerId: p.id,
        displayName: p.displayName,
        totalMs: p.totalMs,
        explosions: p.explosions,
      }));
    },
    getFinalResult() {
      const ranking = computeRanking();
      const { exactTie, tied } = detectTie(ranking);
      const winnerId = state === 'finished' && !exactTie ? ranking[0]?.playerId ?? null : null;
      return {
        ranking,
        winnerId,
        exactTie,
        tiedPlayerIds: tied,
        finished: state === 'finished',
      };
    },
  };

  return handle;
};

export const GAME_CONSTANTS = {
  MAX_PLAYERS,
  MIN_PLAYERS,
  MIN_BOMBS,
  MAX_BOMBS,
  EXPLOSION_TURN_MS,
  RETURN_WINDOW_MS,
  DEFAULT_BASE_DURATION_MS,
  MIN_BASE_DURATION_MS,
  MAX_BASE_DURATION_MS,
  EXTRA_DURATION_MS,
  BONUS_PER_PASS_MS,
} as const;
